import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Mail, Building2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import contactFormBg from "@/assets/contact-form-bg.jpg";
import contactHqBg from "@/assets/contact-hq.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { getContactT } from "@/i18n/about";
import { localizeHref } from "@/lib/localizeHref";

interface Office {
  id: string;
  name: string;
  city: string;
  country: string;
  address: string;
  structuredAddress?: {
    streetAddress: string;
    addressLocality: string;
    postalCode: string;
  };
  phone?: string;
  vat?: string;
  lat: number;
  lng: number;
}

const COUNTRY_FLAGS: Record<string, string> = {
  Belgium: "be",
  Netherlands: "nl",
  "United Kingdom": "gb",
  Switzerland: "ch",
  Spain: "es",
  Poland: "pl",
  Colombia: "co",
  "United States": "us",
};

const CountryFlag = ({ country }: { country: string }) => {
  const code = COUNTRY_FLAGS[country];
  if (!code) return null;
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      width="20"
      height="15"
      alt={`${country} flag`}
      className="inline-block rounded-[2px] shadow-sm shrink-0"
      loading="lazy"
    />
  );
};

const OFFICES: Office[] = [
  {
    id: "wilrijk",
    name: "Belgium - Flanders",
    city: "Wilrijk",
    country: "Belgium",
    address: "Groenenborgerlaan 16, 2610 Wilrijk, Belgium",
    phone: "+32 3 844 45 01",
    vat: "BE0795 392 179",
    lat: 51.1778,
    lng: 4.3997,
  },
  {
    id: "gosselies",
    name: "Belgium - Wallonia",
    city: "Gosselies",
    country: "Belgium",
    address: "Rue Clément Ader 10, 6041 Gosselies, Belgium",
    phone: "+32 3 844 45 01",
    vat: "BE0720512337",
    lat: 50.4627,
    lng: 4.4718,
  },
  {
    id: "breda",
    name: "Netherlands",
    city: "Breda",
    country: "Netherlands",
    address: "Verlengde Poolseweg 46, 4818 Breda, Netherlands",
    phone: "+31 767 410 088",
    vat: "NL853282080B01",
    lat: 51.5882,
    lng: 4.7756,
  },
  {
    id: "barcelona",
    name: "Spain",
    city: "Barcelona",
    country: "Spain",
    address: "Carrer Narcís Monturiol, 14, 08960 Sant Just Desvern, Spain",
    phone: "+34 912 771 076",
    vat: "NIF: B66869918",
    lat: 41.3996,
    lng: 2.1935,
  },
  {
    id: "madrid",
    name: "Spain",
    city: "Madrid",
    country: "Spain",
    address: "Vía de las Dos Castillas, 33, Edificio 7, 28224 Pozuelo de Alarcón, Madrid, Spain",
    phone: "+34 91 277 10 76",
    vat: "CIF: B-85125334",
    lat: 40.4381,
    lng: -3.6795,
  },
  {
    id: "warsaw",
    name: "Poland",
    city: "Warsaw",
    country: "Poland",
    address: "Chelmska 30/34, 00-725 Warsaw, Poland",
    phone: "+48 22 841 16 64",
    vat: "EU: PL 1132679319 - REGON: 141028176",
    lat: 52.2235,
    lng: 20.9828,
  },
  {
    id: "chalgrove",
    name: "United Kingdom",
    city: "Oxfordshire",
    country: "United Kingdom",
    address: "Office 28, Hampden House, Monument Business Park, Chalgrove, OX44 7RW, United Kingdom",
    structuredAddress: {
      streetAddress: "Office 28, Hampden House, Monument Business Park",
      addressLocality: "Chalgrove",
      postalCode: "OX44 7RW",
    },
    phone: "+44 1865 670860",
    vat: "GB5071927613",
    lat: 51.6585,
    lng: -1.0714,
  },
  {
    id: "lausanne",
    name: "Switzerland",
    city: "Lausanne",
    country: "Switzerland",
    address: "Av. de Gratta-Paille 2, 1018 Lausanne, Switzerland",
    phone: "+41215612990",
    lat: 46.5197,
    lng: 6.6323,
  },
  {
    id: "cali",
    name: "Colombia",
    city: "Cali",
    country: "Colombia",
    address: "Av. 2f Nte. #44norte – 107, Valle del Cauca Cali, Colombia",
    phone: "+(57) 350 888 60 31",
    vat: "NIT 900776308-8",
    lat: 3.4516,
    lng: -76.5320,
  },
  {
    id: "newport-beach",
    name: "United States",
    city: "Newport Beach",
    country: "United States",
    address: "20351 Irvine Ave, Suite C6, Newport Beach, CA 92660, US",
    phone: "+31 (0)85 201 72 77",
    lat: 33.6189,
    lng: -117.9289,
  },
];

const HUBSPOT_PORTAL_ID = "7030766";
const HUBSPOT_CONTACT_FORM_ID = "68016d36-003a-48fd-9eb8-8f9cb7128783";

const Contact = () => {
  const lang = useLanguage();
  const t = getContactT(lang);
  const homePath = localizeHref("/", lang);
  const homeLabel = lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home";
  const [selectedOffice, setSelectedOffice] = useState(OFFICES[0]);
  const [mapLoading, setMapLoading] = useState(false);
  const hubspotContainerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(selectedOffice.address)}&z=15&output=embed`;

  const handleSelectOffice = useCallback((office: Office) => {
    if (office.id === selectedOffice.id) return;
    setMapLoading(true);
    setSelectedOffice(office);
  }, [selectedOffice.id]);

  const handleIframeLoad = useCallback(() => {
    setMapLoading(false);
  }, []);

  useEffect(() => {
    if (!hubspotContainerRef.current) return;

    const createForm = () => {
      if (hubspotContainerRef.current && window.hbspt) {
        hubspotContainerRef.current.innerHTML = "";
        window.hbspt.forms.create({
          portalId: HUBSPOT_PORTAL_ID,
          formId: HUBSPOT_CONTACT_FORM_ID,
          region: "na1",
          target: "#hubspot-contact-form",
        });
      }
    };

    if (window.hbspt && scriptLoadedRef.current) {
      createForm();
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="js.hsforms.net/forms/embed/v2.js"]'
    );

    if (existingScript) {
      if (window.hbspt) {
        scriptLoadedRef.current = true;
        createForm();
      } else {
        existingScript.addEventListener("load", () => {
          scriptLoadedRef.current = true;
          createForm();
        });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "//js.hsforms.net/forms/embed/v2.js";
    script.charset = "utf-8";
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      createForm();
    };
    document.head.appendChild(script);
  }, []);

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
        canonical={`https://qbdgroup.com${lang === "es" ? "/es/contacto" : "/en/contact"}`}
      />
      <BreadcrumbJsonLd items={[{ name: t.breadcrumb }]} />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": OFFICES.map((o) => {
              const [street, ...rest] = o.address.split(",").map((s) => s.trim());
              const postalAndCity = rest.length >= 2 ? rest[rest.length - 2] : "";
              const postalMatch = postalAndCity.match(/^(\S+)\s+(.+)$/);
              return {
                "@type": "ProfessionalService",
                "@id": `https://qbdgroup.com/en/contact#${o.id}`,
                name: `QbD Group — ${o.city}`,
                url: "https://qbdgroup.com/en/contact",
                telephone: o.phone,
                vatID: o.vat,
                address: {
                  "@type": "PostalAddress",
                  streetAddress: o.structuredAddress?.streetAddress ?? street,
                  addressLocality: o.structuredAddress?.addressLocality ?? o.city,
                  postalCode: o.structuredAddress?.postalCode ?? (postalMatch ? postalMatch[1] : undefined),
                  addressCountry: o.country,
                },
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: o.lat,
                  longitude: o.lng,
                },
                parentOrganization: {
                  "@type": "Organization",
                  name: "QbD Group",
                  url: "https://qbdgroup.com",
                },
              };
            }),
          }),
        }}
      />
      <Navbar />
      <main>
        {/* Hero / Intro */}
        <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 min-h-[420px] sm:min-h-[480px] flex items-end overflow-hidden">
          <img src={contactHqBg} alt="QbD Group headquarters" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/75" />

          <div className="container mx-auto px-6 lg:px-10 relative z-10">
            <nav className="text-sm text-primary-foreground/50 mb-8">
              <a href={homePath} className="hover:text-primary-foreground/80 transition-colors">{homeLabel}</a>
              <span className="mx-2">&gt;</span>
              <span className="text-primary-foreground/80 font-medium">{t.breadcrumb}</span>
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
               <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground tracking-tight leading-tight mb-6">
                {t.heroTitle}
              </h1>
              <p className="text-primary-foreground/60 leading-relaxed mb-4">
                {t.heroP1}
              </p>
              <p className="text-primary-foreground/60 leading-relaxed">
                {t.heroP2}
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA to form */}
        <section className="bg-qbd-sky/50 border-b border-qbd-blue/10">
          <div className="container mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm sm:text-base text-foreground/80 font-medium">
              {t.ctaText}
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-qbd-blue hover:bg-qbd-blue/90 text-white font-semibold text-sm px-6 py-2.5 rounded-full transition-colors shrink-0"
            >
              <Mail size={16} strokeWidth={2} />
              {t.ctaButton}
            </a>
          </div>
        </section>

        {/* Map + Offices */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-6 lg:px-10">
            <div className={lang === "zh-cn" ? "" : "grid lg:grid-cols-[1fr_420px] gap-10"}>
              {/* Map — hidden in zh-cn because Google Maps is blocked in China */}
              {lang !== "zh-cn" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="rounded-2xl overflow-hidden border border-border shadow-sm min-h-[400px] lg:min-h-[560px] relative"
                >
                  <AnimatePresence>
                    {mapLoading && (
                      <motion.div
                        key="map-loader"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 z-10 bg-background/60 backdrop-blur-sm flex items-center justify-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground font-medium">Loading map…</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <iframe
                    src={mapSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: 400, filter: 'saturate(0.55) brightness(1.05)' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="QbD Group office locations"
                    onLoad={handleIframeLoad}
                  />
                </motion.div>
              )}

              {/* Sidebar – no max-h on mobile to avoid scroll trap */}
              <div>
                <h2 className="text-xl font-bold text-foreground mb-6">{t.locationsTitle}</h2>
                <div className="space-y-3 lg:max-h-[500px] lg:overflow-y-auto lg:pr-2">
                  {OFFICES.map((office) => (
                    <button
                      key={office.id}
                      onClick={() => handleSelectOffice(office)}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                        selectedOffice.id === office.id
                          ? "bg-accent/10 border-accent/30 shadow-sm"
                          : "bg-background border-border hover:border-accent/20 hover:bg-secondary/50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <MapPin
                          size={18}
                          strokeWidth={1.5}
                          className={selectedOffice.id === office.id ? "text-accent mt-0.5 shrink-0" : "text-muted-foreground mt-0.5 shrink-0"}
                        />
                        <div>
                          <p className="font-semibold text-sm text-foreground flex items-center gap-2">
                            <CountryFlag country={office.country} />
                            <span>{office.name}</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{office.address}</p>
                          {office.phone && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                              <Phone size={11} strokeWidth={1.5} /> {office.phone}
                            </p>
                          )}
                          {office.vat && (
                            <p className="text-xs text-muted-foreground/70 mt-0.5">{office.vat}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="relative py-16 sm:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img src={contactFormBg} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/85" />
          </div>
          <div className="container mx-auto px-6 lg:px-10 relative z-10">
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Mail size={36} strokeWidth={1.5} className="text-accent mb-5" />
                <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight leading-tight mb-5">
                  {t.formTitle}
                </h2>
                <p className="text-primary-foreground/60 leading-relaxed mb-4">
                  {t.formP1}
                </p>
                <p className="text-primary-foreground/60 leading-relaxed mb-8">
                  {t.formP2}
                </p>
                <div className="space-y-4 mb-10">
                  <div className="flex items-center gap-3">
                    <Phone size={18} strokeWidth={1.5} className="text-accent" />
                    <a href="tel:+3238444501" className="text-sm text-primary-foreground hover:text-accent transition-colors">+32 (0)3 844 45 01</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} strokeWidth={1.5} className="text-accent" />
                    <a href="mailto:info@qbdgroup.com" className="text-sm text-primary-foreground hover:text-accent transition-colors">info@qbdgroup.com</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 size={18} strokeWidth={1.5} className="text-accent" />
                    <span className="text-sm text-primary-foreground/60">Groenenborgerlaan 16, 2610 Wilrijk, Belgium</span>
                  </div>
                </div>

                {/* Key differentiators */}
                <div className="border-t border-primary-foreground/10 pt-8">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/40 mb-6">{t.whyChooseUs}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      {
                        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/></svg>,
                        title: t.whyItems[0].title,
                        desc: t.whyItems[0].desc,
                      },
                      {
                        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5"/></svg>,
                        title: t.whyItems[1].title,
                        desc: t.whyItems[1].desc,
                      },
                      {
                        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
                        title: t.whyItems[2].title,
                        desc: t.whyItems[2].desc,
                      },
                      {
                        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>,
                        title: t.whyItems[3].title,
                        desc: t.whyItems[3].desc,
                      },
                      {
                        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
                        title: t.whyItems[4].title,
                        desc: t.whyItems[4].desc,
                      },
                      {
                        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 text-accent"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
                        title: t.whyItems[5].title,
                        desc: t.whyItems[5].desc,
                      },
                    ].map((item) => (
                      <div key={item.title} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center shrink-0 mt-0.5">
                          {item.icon}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-primary-foreground/90">{item.title}</p>
                          <p className="text-xs text-primary-foreground/50 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-background rounded-2xl border border-border p-8 sm:p-10 shadow-sm"
              >
                <div
                  id="hubspot-contact-form"
                  ref={hubspotContainerRef}
                  className="min-h-[500px] [&_label]:text-sm [&_label]:font-medium [&_label]:text-foreground/80 [&_label]:font-[Open_Sans,sans-serif] [&_input]:rounded-lg [&_input]:border-border [&_input]:font-[Open_Sans,sans-serif] [&_input]:text-sm [&_select]:rounded-lg [&_select]:border-border [&_select]:font-[Open_Sans,sans-serif] [&_select]:text-sm [&_textarea]:rounded-lg [&_textarea]:border-border [&_textarea]:font-[Open_Sans,sans-serif] [&_textarea]:text-sm [&_.hs-submit]:mt-4 [&_.hs-button]:!bg-accent [&_.hs-button]:!border-accent [&_.hs-button]:rounded-full [&_.hs-button]:font-semibold [&_.hs-button]:font-[Open_Sans,sans-serif] [&_.hs-button]:px-8 [&_.hs-button]:py-3 [&_.hs-error-msgs]:text-xs [&_.hs-error-msgs]:text-destructive [&_.hs-form-field]:mb-4"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Removed "Wherever you are" closing — the map+offices section already communicates global presence */}
      </main>
      <Footer />
    </>
  );
};

export default Contact;
